import Papa from 'papaparse';
import { formatDate } from './dateFormatter';

type ExportCsvOptions = {
  format?: string;
  fileName?: string;
  headers?: readonly string[];
};

export function filterCsvDataByVisibleColumns(
  rows: Array<Record<string, unknown>>,
  visibleColumnIds: readonly string[],
  exportKeysByColumn: Readonly<Record<string, readonly string[]>>,
) {
  const visibleKeys = new Set(visibleColumnIds.flatMap((columnId) => exportKeysByColumn[columnId] ?? []));

  return rows.map((row) => Object.fromEntries(Object.entries(row).filter(([exportKey]) => visibleKeys.has(exportKey))));
}

function downloadCsv(csv: string, fileName?: string) {
  // Excel-friendly UTF-8 (handles Japanese)
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  const enforcedName = fileName ? fileName.replace(/\.csv$/i, '') + '.csv' : 'Report.csv';

  a.href = url;
  a.download = enforcedName;
  document.body.appendChild(a);
  a.click();
  a.remove();

  URL.revokeObjectURL(url);
}

function toSimpleString(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function maybeFormatEpochMs(value: unknown, format?: string): string | number | boolean | null | undefined {
  if (typeof value === 'number' && value > 1_000_000_000_000) {
    return formatDate(value, format);
  }
  return value as string | number | boolean | null | undefined;
}

export const exportCsvSingleFilePapa = (sheetData: Array<Record<string, unknown>>, options: ExportCsvOptions = {}) => {
  const { format, fileName, headers } = options;

  // Convert all values to simple strings for display
  const formattedData = sheetData.map((row) => {
    const out: Record<string, string> = {};

    for (const key of Object.keys(row)) {
      const value = maybeFormatEpochMs(row[key], format);
      // Force string conversion to prevent Excel from interpreting as number
      out[key] = '\t' + toSimpleString(value);
    }

    return out;
  });

  const csv = Papa.unparse(headers ? { fields: [...headers], data: formattedData } : formattedData, {
    header: true,
    quotes: true,
    skipEmptyLines: true,
  });

  downloadCsv(csv, fileName);
};
